import React, { useState, useRef, useEffect } from 'react';
import UnifiedWebSocketService from '../../services/UnifiedWebSocketService';
import CaseInfoDisplay from '../shared/CaseInfoDisplay';
import VisitNotesPanel from '../shared/VisitNotesPanel';
import NoteViewer from '../shared/NoteViewer';
import NoteGenerationPanel from './NoteGenerationPanel';
import { fetchCaseVisitNotes, fetchCaseTranscripts, convertNoteFormat } from '../../services/api';
import { getAuthData } from '../../utils/sessionStorage';
import { fetchBackendApi } from '../../utils/apiUtils';

// Utility function to create artificial delays (e.g., for UX feedback)
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * VisitNotesEditor - AI-generated visit notes editor
 * Allows editing, formatting, and exporting of medical documentation
 * Can load historical notes from previous sessions
 */
const VisitNotesEditor = ({ soapNote, onUpdateNote, providerInfo, authData, caseId, sessionToLoad = null }) => {
  const [editedNote, setEditedNote] = useState(soapNote || '');
  const [realtimeNote, setRealtimeNote] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [notification, setNotification] = useState(null);
  const [loadedSession, setLoadedSession] = useState(null);
  const [isLoadingNote, setIsLoadingNote] = useState(false);
  const [visitNotes, setVisitNotes] = useState([]);
  const [showVisitNotes, setShowVisitNotes] = useState(false);
  const [availableTranscripts, setAvailableTranscripts] = useState([]);
  const [showNoteSettings, setShowNoteSettings] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [noteFormats, setNoteFormats] = useState({}); // { noteId: { html, plain_text, rtf } }
  const [convertingNotes, setConvertingNotes] = useState({}); // { noteId: 'loading' | 'error' }
  const [convertErrors, setConvertErrors] = useState({}); // { noteId: { error: '...' } }
  const [sendingNoteId, setSendingNoteId] = useState(null); // Track which note is being sent

  // Update edited note when soapNote prop changes
  useEffect(() => {
    setEditedNote(soapNote || realtimeNote || '');
  }, [soapNote, realtimeNote]);

  // Function to load visit notes (extracted for reuse)
  const loadCaseVisitNotes = async () => {
    // Get case ID from localStorage as single source of truth
    const storedCaseId = localStorage.getItem('currentCaseId') || caseId;
    // console.log('[VisitNotesEditor] Using case ID from localStorage:', storedCaseId);
    
    if (!storedCaseId) {
      // console.log('[VisitNotesEditor] No caseId in localStorage or props, skipping API call');
      return;
    }
    
    // console.log('[VisitNotesEditor] Making API call for caseId:', storedCaseId);
    
    try {
      const auth = authData || getAuthData();
      const apiToken = auth?.api_token;
      
      if (!apiToken) return;
      
      const response = await fetchCaseVisitNotes(apiToken, storedCaseId);
      
      // console.log('[VisitNotesEditor] Visit notes API Response:', response);
      if (response.success && response.visit_notes) {
        // console.log('[VisitNotesEditor] Visit notes array:', response.visit_notes);
        setVisitNotes(response.visit_notes);
        
        // Auto-load the latest visit note if available
        const latestNote = response.visit_notes.find(note => note.is_latest);
        if (latestNote && !editedNote && !soapNote && !realtimeNote) {
          setEditedNote(latestNote.content || '');
          setLastUpdated(new Date(latestNote.created_at));
        }
      } else {
        console.log('[VisitNotesEditor] No visit notes found in response');
      }
    } catch (error) {
      console.error('Failed to load case visit notes:', error);
    }
  };

  // Load all visit notes for case when caseId is provided
  useEffect(() => {
    // console.log('[VisitNotesEditor] useEffect triggered with caseId prop:', caseId);
    loadCaseVisitNotes();
  }, [caseId, authData]);

  // Load available transcripts for note generation
  useEffect(() => {
    const loadCaseTranscripts = async () => {
      const storedCaseId = localStorage.getItem('currentCaseId') || caseId;
      if (!storedCaseId) return;

      try {
        const auth = authData || getAuthData();
        const apiToken = auth?.api_token;
        if (!apiToken) return;
        
        const transcriptsData = await fetchCaseTranscripts(apiToken, storedCaseId);
        setAvailableTranscripts(transcriptsData.transcripts || []);
      } catch (error) {
        showNotificationMessage('Failed to load transcripts for note generation:', error);
      }
    };

    loadCaseTranscripts();
  }, [caseId, authData]);

  // Notification helper
  const showNotificationMessage = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Load historical note when sessionToLoad changes
  useEffect(() => {
    const loadHistoricalNote = async () => {
      if (!sessionToLoad?.visit_note_id) return;
      
      setIsLoadingNote(true);
      try {
        const auth = authData || getAuthData();
        const apiToken = auth?.api_token;
        
        if (!apiToken) {
          showNotificationMessage('Authentication required to load note', 'error');
          return;
        }
        
        const noteData = await fetchVisitNotes(apiToken, sessionToLoad.visit_note_id);
        setLoadedSession(sessionToLoad);
        setEditedNote(noteData.content || noteData.note_content || '');
        setLastUpdated(noteData.created_at ? new Date(noteData.created_at) : null);
        showNotificationMessage(`Loaded note from session ${sessionToLoad.session_id.slice(0, 8)}...`, 'success');
      } catch (error) {
        // console.error('Failed to load historical note:', error);
        showNotificationMessage('Failed to load note', 'error');
      } finally {
        setIsLoadingNote(false);
      }
    };
    
    loadHistoricalNote();
  }, [sessionToLoad, authData]);

  // Setup WebSocket listeners for real-time visit note updates
  useEffect(() => {
    const handleVisitNoteReady = (data) => {
      // console.log('Received visit note ready:', data);
      setRealtimeNote(data.note_content || data.content || '');
      setLastUpdated(new Date(data.timestamp));
      setIsGenerating(false);
      // Update parent component if provided
      if (onUpdateNote) {
        onUpdateNote(data.note_content || data.content || '');
      }
    };

    const handleProcessingStarted = (data) => {
      // console.log('Visit note processing started');
      setIsGenerating(true);
    };

    const handleGenerationError = (data) => {
      // console.log('Visit note generation error:', data);
      setIsGenerating(false);
      setNotification({ 
        message: data.error || 'Failed to generate visit note', 
        type: 'error' 
      });
      setTimeout(() => setNotification(null), 3000);
    };

    // Add WebSocket listeners using the unsubscribe pattern
    const unsubscribeNoteReady = UnifiedWebSocketService.on('visitNoteReady', handleVisitNoteReady);
    const unsubscribeProcessing = UnifiedWebSocketService.on('processingStarted', handleProcessingStarted);
    const unsubscribeError = UnifiedWebSocketService.on('noteGenerationError', handleGenerationError);

    // Cleanup listeners on unmount using the returned unsubscribe functions
    return () => {
      unsubscribeNoteReady();
      unsubscribeProcessing();
      unsubscribeError();
    };
  }, [onUpdateNote]);

  // Handle copy to clipboard
  const handleCopy = async () => {
    const content = editedNote || soapNote || '';
    if (content) {
      try {
        await navigator.clipboard.writeText(content);
        showNotification('Visit note copied to clipboard!', 'success');
      } catch (err) {
        showNotification('Failed to copy to clipboard', 'error');
      }
    }
  };

  // Handle export (placeholder for future implementation)
  const handleExport = () => {
    const content = editedNote || soapNote || '';
    if (content) {
      // Create downloadable file
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `visit-note-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle generate visit note
  const handleGenerateNote = () => {
    if (UnifiedWebSocketService.isConnected) {
      UnifiedWebSocketService.generateNote();
      setIsGenerating(true);
      showNotification('Generating visit note...', 'info');
    } else {
      // console.error('WebSocket not authenticated - cannot generate visit note');
      showNotification('Hmm, I am having trouble generating a note. Are you logged in?', 'error');
    }
  };

  // Parse note content into sections for Core Server
  const parseNoteIntoSections = (noteText) => {
    if (!noteText) return {};
    
    const sections = {};
    
    // Common medical note section patterns
    const sectionPatterns = [
      { key: 'chiefComplaint', patterns: ['Chief Complaint', 'CC', 'Complaint'] },
      { key: 'historyOfPresentIllness', patterns: ['History of Present Illness', 'HPI', 'Present Illness'] },
      { key: 'reviewOfSystems', patterns: ['Review of Systems', 'ROS', 'Systems Review'] },
      { key: 'pastMedicalHistory', patterns: ['Past Medical History', 'PMH', 'Medical History'] },
      { key: 'physicalExamination', patterns: ['Physical Examination', 'Physical Exam', 'PE', 'Examination'] },
      { key: 'assessment', patterns: ['Assessment', 'Impression', 'Diagnosis'] },
      { key: 'plan', patterns: ['Plan', 'Treatment Plan'] },
      { key: 'assessmentAndPlan', patterns: ['Assessment and Plan', 'Assessment & Plan', 'A&P'] }
    ];
    
    // Split the note by lines and process
    const lines = noteText.split('\n');
    let currentSection = null;
    let currentContent = [];
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;
      
      // Check if this line starts a new section
      let foundSection = null;
      for (const section of sectionPatterns) {
        for (const pattern of section.patterns) {
          if (trimmedLine.toLowerCase().includes(pattern.toLowerCase() + ':') || 
              trimmedLine.toLowerCase().startsWith(pattern.toLowerCase())) {
            foundSection = section.key;
            break;
          }
        }
        if (foundSection) break;
      }
      
      if (foundSection) {
        // Save previous section if it exists
        if (currentSection && currentContent.length > 0) {
          sections[currentSection] = currentContent.join('\n').trim();
        }
        
        // Start new section
        currentSection = foundSection;
        currentContent = [trimmedLine];
      } else if (currentSection) {
        // Add to current section
        currentContent.push(trimmedLine);
      } else {
        // No section yet, might be general content
        if (!sections.general) sections.general = '';
        sections.general += trimmedLine + '\n';
      }
    }
    
    // Save the last section
    if (currentSection && currentContent.length > 0) {
      sections[currentSection] = currentContent.join('\n').trim();
    }
    
    return sections;
  };

  // Handle format conversion for a note
  const handleConvertFormat = async (noteId, format) => {
    try {
      setConvertingNotes(prev => ({ ...prev, [noteId]: 'loading' }));
      setConvertErrors(prev => ({ ...prev, [noteId]: null }));

      const auth = authData || getAuthData();
      const apiToken = auth?.api_token;

      if (!apiToken) {
        throw new Error('Authentication required');
      }

      const response = await convertNoteFormat(apiToken, noteId, format);

      if (response.success) {
        // Update noteFormats state
        setNoteFormats(prev => ({
          ...prev,
          [noteId]: {
            ...prev[noteId],
            [format]: response[format]
          }
        }));
        
        // Also update visitNotes to ensure NoteViewer receives the updated formats
        setVisitNotes(prev => prev.map(note => {
          if (note.id === noteId) {
            return {
              ...note,
              formats: {
                ...note.formats,
                [format]: response[format]
              }
            };
          }
          return note;
        }));
        
        setConvertingNotes(prev => ({ ...prev, [noteId]: 'idle' }));
        showNotification(`${format.toUpperCase()} format generated successfully!`, 'success');
      } else {
        throw new Error(response.error || 'Conversion failed');
      }
    } catch (error) {
      console.error(`Error converting note to ${format}:`, error);
      setConvertingNotes(prev => ({ ...prev, [noteId]: 'error' }));
      setConvertErrors(prev => ({ ...prev, [noteId]: { error: error.message } }));
      showNotification(`Failed to generate ${format.toUpperCase()} format`, 'error');
    }
  };

  // Handle sending note to Core Server
  const handleSendNoteToCoreServer = async (note) => {
    const noteContent = formatNoteDisplay(note.content) || '';

    if (!noteContent.trim()) {
      showNotification('No note to send', 'error');
      return;
    }

    try {
      setSendingNoteId(note.id);
      // DISABLED - postMessage iframe mode (AR 02/08/26)
      // const sectionsToSend = parseNoteIntoSections(noteContent);
      // const payload = {
      //   type: 'VISIT_NOTE_SECTIONS_UPDATE',
      //   data: {
      //     noteId: note.id,
      //     sections: sectionsToSend,
      //     fullNoteContent: noteContent.trim(),
      //     timestamp: new Date().toISOString(),
      //     wordCount: noteContent.trim().split(/\s+/).length,
      //     structured: false
      //   }
      // };

      // if (window.parent !== window) {
      //   // Embedded in iframe - use postMessage
      //   window.parent.postMessage(payload, '*');
      //   showNotification('Note sent to Core Server successfully!', 'success');
      // } else {
        // Standalone mode - call backend API to trigger callback
        const auth = authData || getAuthData();
        const apiToken = auth?.api_token;
        if (!apiToken) {
          showNotification('Authentication required to send note', 'error');
          setSendingNoteId(null);
          return;
        }
        const response = await fetchBackendApi(
          `/api/provider/note/${note.id}/send/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiToken}`
            }
          }
        );
        await delay(3000); // Pauses *this function* for 3 seconds
        if (response.ok) {
          showNotification('Note sent to destination successfully!', 'success');
        } else {
          const error = await response.json();
          showNotification(`Failed to send note: ${error.detail || error.error || 'Unknown error'}`, 'error');
        }
      // }
    } catch (err) {
      showNotification('Failed to send note to Core Server: ' + err.message, 'error');
    } finally {
      setSendingNoteId(null);
    }
  };

  // Handle editing a note - DISABLED (editing functionality needs to be fixed)
  // const handleEditNote = (note) => {
  //   setEditingNote(note);
  //   setShowVisitNotes(true);
  // };

  // Handle selecting a visit note (removed since we use VisitNotesPanel now)

  // Format note sections for better display
  const formatNoteDisplay = (note) => {
    if (!note) return '';

    // Handle structured notes (new format with {content: "..."} objects)
    if (typeof note === 'object' && !Array.isArray(note)) {
      const parts = [];
      for (const [key, value] of Object.entries(note)) {
        if (value) {
          // Format the section name (convert camelCase/snake_case to Title Case)
          const sectionName = key
            .replace(/([A-Z])/g, ' $1') // Add space before capital letters
            .replace(/_/g, ' ') // Replace underscores with spaces
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');

          // If section has a content field (new format), use it
          if (typeof value === 'object' && value.content) {
            parts.push(`${sectionName}:\n${value.content}`);
          } else if (typeof value === 'string') {
            // Old format or plain string value
            parts.push(`${sectionName}:\n${value}`);
          }
        }
      }
      return parts.join('\n\n');
    }

    // Handle string notes (old format or plain text)
    if (typeof note === 'string') {
      return note
        .replace(/^(SUBJECTIVE|OBJECTIVE|ASSESSMENT|PLAN):/gm, '\n$1:')
        .replace(/^(Chief Complaint|History of Present Illness|Review of Systems|Physical Examination|Assessment and Plan):/gm, '\n$1:')
        .trim();
    }
    return '';
  };

  const displayNote = formatNoteDisplay(editedNote || soapNote || realtimeNote || '');
  const hasContent = Boolean(displayNote.trim());

  return (
    <div className="visit-notes-editor" style={{maxHeight: '80vh', overflowY: 'auto', overflowX: 'hidden'}}>
      {/* Header similar to LiveTranscription */}
      <div className="d-flex d-flex-inline justify-content-between p-3 p-md-4">
        <div className="header-left">
          <h3><b>Visit Notes</b></h3>
          {isGenerating && (
            <div className="session-info">
              <span className="generating-dot"></span> Generating...
            </div>
          )}
          {lastUpdated && !isGenerating && (
            <div className="session-info">
             <span className="session-type">Updated {lastUpdated.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
              <button
                className="btn btn-warning btn-sm"
                onClick={() => setShowNoteSettings(!showNoteSettings)}
              ><i className="fas fa-plus me-2 d-none d-md-inline"></i>
                {showNoteSettings ? 'X' : 'Create New'} </button>
          </div> 

      {/* Notification */}
      {notification && (
        <div className={`alert alert-${notification.type === 'error' ? 'danger' : notification.type} mx-3 mt-2`}>
          {notification.message}
        </div>
      )}

      <div className="editor-content" style={{maxHeight: '70vh', overflowY: 'auto'}}>
          {/* Case Information Display */}
          <CaseInfoDisplay
            caseId={caseId}
            compact={false}
            showPatientInfo={true}
            showProviderInfo={true}
            showSessionInfo={true}
            className="mx-3 mt-2"
          />

          {/* Note Generation Settings */}
          {/* <div className="note-generation-section bg-warning rounded p-3 mx-3 mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4>Create Note</h4>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowNoteSettings(!showNoteSettings)}
              >
                {showNoteSettings ? 'Hide' : 'Settings'}
              </button>
            </div>
          </div> */}

          {/* Historical Visit Notes Section */}
          {visitNotes.length > 0 && (
            <div className="w-100 px-3 px-md-3 mt-3">
              <div className="d-flex justify-content-between align-items-center px-1 py-2">
                Available Notes ({visitNotes.length})
              </div>
              {visitNotes.map((note) => (
                <NoteViewer
                  key={note.id}
                  note={note}
                  mode="compact"
                  formats={note.formats || noteFormats[note.id]}
                  onConvertFormat={(format) => handleConvertFormat(note.id, format)}
                  convertStatus={convertingNotes[note.id] || 'idle'}
                  convertError={convertErrors[note.id]}
                  onSendToCore={handleSendNoteToCoreServer}
                  isSending={sendingNoteId === note.id}
                />
              ))}
            </div>
          )}

          {/* Empty state when no visit notes */}
          {visitNotes.length === 0 && (
            <div className="empty-state text-center py-5 mx-3 mt-3">
              <div className="empty-icon">📝</div>
              <p>No notes found</p>
              <small>Select a case to view notes, or create one</small>
            </div>
          )}
        
      </div>

      

      {/* Visit Notes Side Panel */}
      <VisitNotesPanel
        isOpen={showVisitNotes}
        noteId={editingNote?.id || null}
        onClose={() => {
          setShowVisitNotes(false);
          setEditingNote(null);
        }}
        onSave={(updatedContent) => {
          setEditedNote(updatedContent);
          if (onUpdateNote) {
            onUpdateNote(updatedContent);
          }
          // Refresh the visit notes list
          loadCaseVisitNotes();
          setShowVisitNotes(false);
          setEditingNote(null);
        }}
        authData={authData || getAuthData()}
      />

      {/* Note Generation Settings Slide-in Panel */}
      <NoteGenerationPanel
        isOpen={showNoteSettings}
        caseId={caseId}
        authData={authData || getAuthData()}
        providerInfo={providerInfo}
        transcripts={availableTranscripts}
        onClose={() => setShowNoteSettings(false)}
        onNoteGenerated={(response) => {
          // Check if response includes structured sections
          let noteContent = response.content || '';
          
          if (response.structured && response.sections) {
            // Store structured sections as JSON for the editor to handle
            noteContent = JSON.stringify(response.sections, null, 2);
          }
          
          setEditedNote(noteContent);
          setLastUpdated(new Date());
          if (onUpdateNote) {
            onUpdateNote(noteContent);
          }
          
          const message = response.structured 
            ? 'Structured note generated successfully!' 
            : 'Note generated successfully!';
          showNotificationMessage(message, 'success');
          setShowNoteSettings(false); // Close panel after generation
          // Refresh the visit notes list to show the new note
          loadCaseVisitNotes();
        }}
        autoGenerate={true}
      />
    </div>
  );
};

export default VisitNotesEditor;